import { create } from 'zustand';

import type { ToastVariant } from '@/components/ui/toast';

export interface ToastMessage {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastState {
  toasts: ToastMessage[];
  push: (toast: Omit<ToastMessage, 'id'>) => string;
  dismiss: (id: string) => void;
}

const genId = (): string => Math.random().toString(36).slice(2, 10);

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = genId();
    set((state) => ({ toasts: [...state.toasts, { id, duration: 5000, ...toast }] }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

export interface ToastApi {
  toast: (toast: Omit<ToastMessage, 'id'>) => string;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
}

export function useToast(): ToastApi {
  const push = useToastStore((s) => s.push);
  const dismiss = useToastStore((s) => s.dismiss);

  return {
    toast: push,
    success: (title, description) =>
      push({
        title,
        ...(description !== undefined ? { description } : {}),
        variant: 'success',
      }),
    error: (title, description) =>
      push({
        title,
        ...(description !== undefined ? { description } : {}),
        variant: 'destructive',
      }),
    dismiss,
  };
}

export function toast(options: Omit<ToastMessage, 'id'>): string {
  return useToastStore.getState().push(options);
}
